import { type FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HourglassIcon from '@assets/img/icons/hourglass.svg?react';
// import PeopleIcon from '@assets/img/icons/people.svg?react';
import cn from 'classnames';
import { AnimatePresence, motion, transform } from 'framer-motion';
import { useOnClickOutside } from 'usehooks-ts';

import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { timeStringFormat } from '@/common/lib/timer';
import { StringSmall, StringXSmall } from '@/components/Typography/Typography';
import { Info } from '@/components/ui/Info/Info';

import { infoText } from '../config';
import { LeagueBanner } from '../LeagueBanner/LeagueBanner';
import { OffersForLeague } from '../OffersForLeague/OffersForLeague';

import css from './styles.module.scss';

interface LeagueEventInfoProps {
  isEventActive: boolean;
  players: number;
  timeToEnd: number;
  interval: number;
  activeLeagueRank: number;
}

const LeagueEventInfo: FC<LeagueEventInfoProps> = ({
  isEventActive,
  timeToEnd,
  interval,
  activeLeagueRank,
}) => {
  const seconds = useCountDownTimer(timeToEnd < 0 ? 0 : timeToEnd);
  const [isInfoDetailOpen, setIsInfoDetailOpen] = useState(false);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(ref, () => {
    setIsInfoDetailOpen(false);
  });

  const percentage = transform([0, interval], ['0%', '100%']);

  return (
    <div
      className={cn('w-full flex gap-1 mt-1 mb-2', !isEventActive && 'flex-1')}
    >
      {isEventActive ? (
        <>
          <div className="rounded-lg bg-white/10 h-8  justify-center flex gap-1 items-center relative overflow-hidden w-full">
            <HourglassIcon className="w-4 h-4 shrink-0" />
            <StringXSmall weight="medium">{t('Event')}:</StringXSmall>
            <StringSmall
              weight="bold"
              className={cn(css.players, '!text-accent !leading-none')}
            >
              {timeStringFormat(seconds * 1000, {
                template: '{days}d : {hours}h : {minutes}m',
              })}
            </StringSmall>
            <div
              style={{ width: percentage(seconds) }}
              className={css.progress}
            ></div>
          </div>
          {/* <div className="rounded-lg bg-white/10 h-8 flex-1 flex gap-1 items-center px-2">
            <PeopleIcon className="w-4 h-4 shrink-0 mr-1" />
            <StringXSmall weight="medium">{t('Players')}:</StringXSmall>
            <StringSmall
              weight="bold"
              className={cn(css.players, '!text-accent !leading-none')}
            >
              {players}
            </StringSmall>
          </div> */}
          <div ref={ref} className="relative">
            <AnimatePresence>
              {isInfoDetailOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={css.infoDetail}
                >
                  <StringXSmall weight="bold">
                    {t('How does it work?')}
                  </StringXSmall>
                  <ul className="flex flex-col gap-2">
                    {Array.from({ length: 5 }).map((_, index) => {
                      const text = infoText[index];

                      return (
                        <li
                          key={`info-text-${index}`}
                          className="text-[0.5625rem] text-white/60"
                        >
                          - {t(text)}
                        </li>
                      );
                    })}
                  </ul>
                  <span className={css.polygon}></span>
                </motion.div>
              )}
            </AnimatePresence>
            <Info onClick={() => setIsInfoDetailOpen((state) => !state)} />
          </div>
        </>
      ) : (
        <div className="w-full flex flex-1 flex-col items-center">
          <LeagueBanner
            className="mb-6"
            activeLeagueRank={activeLeagueRank}
            timeToEnd={timeToEnd}
          />
          <OffersForLeague />
        </div>
        // <WaitForLeagueEventStart
        //   activeLeagueRank={activeLeagueRank}
        //   timeToEnd={timeToEnd}
        // />
        // <div className="p-2 w-full bg-white/10 flex flex-col items-center gap-2 rounded-lg relative overflow-hidden">
        //   <StringMain weight="bold" className="leading-1.2">
        //     {t('New Event starts soon!')}
        //   </StringMain>
        //   <div className="flex gap-[1.375rem]">
        //     {Array.from({ length: 4 }).map((_, index) => {
        //       const timeUnit = timeUnitObj[index as 0 | 1 | 2 | 3];
        //       const timeObj = milisecondsToTime(seconds * 1000);
        //       const timeValue = timeObj[timeUnit as keyof typeof timeObj];
        //       const isDaysZero = timeUnit === 'days' && timeValue === '00';

        //       return (
        //         !isDaysZero && (
        //           <div
        //             key={`winner-position-${index}`}
        //             className={cn(
        //               'flex flex-col items-center gap-1 min-w-12',
        //               index !== 3 && css.colon,
        //             )}
        //           >
        //             <H1
        //               weight="bold"
        //               className={cn(css.timeTitle, '!text-accent')}
        //             >
        //               {timeObj[timeUnit as keyof typeof timeObj]}
        //             </H1>
        //             <StringXSmall
        //               weight="regular"
        //               className={cn(css.timeDescr, '!text-white/40')}
        //             >
        //               {t(timeUnit)}
        //             </StringXSmall>
        //           </div>
        //         )
        //       );
        //     })}
        //   </div>
        //   <div
        //     style={{ width: percentage(timeToEnd) }}
        //     className={css.progress}
        //   ></div>
        // </div>
      )}
    </div>
  );
};

export { LeagueEventInfo };
